import { z } from 'zod';

/**
 * A conversation's name, as the sidebar's inline rename field holds it.
 *
 * Its own schema and its own form, deliberately not the composer's. `RawForm`
 * renders a real `<form>` and a form cannot contain another, so sharing
 * `ChatFormSchema` would have meant a chat name and a prompt living in one set of
 * values - two unrelated fields validated together, where clearing the composer
 * would have had to know not to clear a name someone was halfway through typing.
 * `Spark` moves the sidebar outside the chat form for this reason; see `chatForm`
 * there.
 *
 * **No `min(1)`, for the same reason the composer has none.** This used to carry one,
 * on the argument that the field opens pre-filled with the current name and is only
 * ever emptied on the way to a mistake. That stopped being true the moment a
 * conversation could exist without a name: an upload with no `chatId` mints a chat
 * before any prompt, and `createChatName` is `nullIfBlank(text)`, so a prompt that is
 * only an attachment names nothing. The field then opens **empty** - and `FormField`
 * reveals an error as soon as the input is touched - so a rename on such a row greeted the
 * reader with "Enter a name" before they had done anything wrong. (`submitOnChange` made it
 * instant by submitting unprompted; it has since been removed, but touching the field is
 * enough on its own, so the rule would still be wrong.)
 *
 * An empty field is this one's resting state too, then, and a resting state is not a
 * mistake. Emptying the name simply does not rename: `ChatHistoryRename`'s `onSubmit`
 * ignores a falsy value, so the conversation keeps the name it had. There is nothing to
 * warn about, because nothing is lost.
 *
 * `.trim()` stays, and does two jobs. It matches the server, which trims in
 * `QPUpdateChatNameRequestDto.setName` before applying `@NotBlank`, so a name of spaces
 * is rejected the same way on both sides. And it is what makes a whitespace-only entry
 * arrive at the handler as `''`, where the falsy guard already ignores it - so that case
 * needs no rule of its own.
 */
export const ChatHistoryRenameFormSchema = z.object({
  name: z
    .string()
    .trim()
    /*
      `CHAT_NAME_MAX_LENGTH` on `QualifiedPipelineService`, which is 40. The only rule this
      field really has.

      **The server refuses an over-long rename rather than truncating it** (checked against the
      DTO, 2026-08-18 - this comment said the opposite before and was wrong).
      `QPUpdateChatNameRequestDto` carries `@NotBlank @Size(max = CHAT_NAME_MAX_LENGTH)` and the
      controller takes `@Valid`, so 41 characters is a `400` and never reaches storage. The
      truncate-and-ellipsis behaviour is a different path: `toChatName`, whose only callers are
      inside `createChatName`, so it shapes names the *server* invents and never one typed here.

      Enforced here anyway, and it earns its keep for a reason worth knowing: the generated
      `UpdateQualifiedPipelineNameRequest` carries no upper bound at all - `@Size(max = ...)` does
      not reach the OpenAPI spec - so nothing on the client side but this stops the reader
      writing 60 characters and getting a rejected request with no explanation.

      Note the DTO's setter trims before validating, so trailing spaces cannot push a
      just-fitting name over.
    */
    .max(40, { message: 'Names are limited to 40 characters' }),
});

export type ChatHistoryRenameForm = z.infer<typeof ChatHistoryRenameFormSchema>;
